import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useAuth } from '../../../features/auth/auth-context';
import { customersApi } from '../../../features/customers/customers.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';

import styles from './AdminPortal.module.css';
import { CustomerDocumentsPanel } from './CustomerDocumentsPanel';

export function AdminCustomerDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();

  const customer = useQuery({
    queryKey: ['admin', 'customers', id],
    queryFn: () => customersApi.getAdmin(id),
  });

  const addresses = useQuery({
    queryKey: ['admin', 'customers', id, 'addresses'],
    queryFn: () => customersApi.listAdminAddresses(id),
    enabled: customer.isSuccess,
  });

  const bankAccounts = useQuery({
    queryKey: ['admin', 'customers', id, 'bank-accounts'],
    queryFn: () => customersApi.listAdminBankAccounts(id),
    enabled: customer.isSuccess,
  });

  const applications = useQuery({
    queryKey: ['admin', 'customers', id, 'applications'],
    queryFn: () => applicationsApi.listAdmin({ customerId: id, pageSize: 50 }),
    enabled: customer.isSuccess && can(PERMISSIONS.SUBSCRIPTIONS_READ),
  });

  if (customer.isPending) {
    return <PageSpinner label="Loading customer" />;
  }

  if (customer.isError) {
    return <Alert tone="error">{toDisplayMessage(customer.error)}</Alert>;
  }

  const data = customer.data;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Customer record</p>
          <h1 className={styles.pageTitle}>
            {data.firstName} {data.lastName}
          </h1>
          <p className={styles.pageSubtitle}>{data.customerNumber}</p>
        </div>
        <StatusBadge status={data.status} />
      </header>

      <div className={styles.contentGrid}>
        <section className={styles.card} aria-labelledby="customer-profile-heading">
          <h2 className={styles.cardTitle} id="customer-profile-heading">
            Live identity and contact
          </h2>
          <p className={styles.notice}>
            This is the current customer profile. It is not a frozen historical contract-party
            record.
          </p>
          <dl className={styles.definitionList}>
            <dt>Customer number</dt>
            <dd>{data.customerNumber}</dd>
            <dt>Phone</dt>
            <dd>{data.phoneNumber}</dd>
            <dt>Alternate phone</dt>
            <dd>{data.alternatePhone ?? '—'}</dd>
            <dt>Date of birth</dt>
            <dd>
              {data.dateOfBirth === null ? '—' : new Date(data.dateOfBirth).toLocaleDateString()}
            </dd>
            <dt>Occupation</dt>
            <dd>{data.occupation ?? '—'}</dd>
            <dt>Employer</dt>
            <dd>{data.employerName ?? '—'}</dd>
          </dl>
        </section>

        <section className={styles.card} aria-labelledby="customer-status-heading">
          <h2 className={styles.cardTitle} id="customer-status-heading">
            Account readiness
          </h2>
          <dl className={styles.definitionList}>
            <dt>Profile status</dt>
            <dd>
              <StatusBadge status={data.status} />
            </dd>
            <dt>Onboarding</dt>
            <dd>{formatStatusLabel(data.onboardingStage)}</dd>
            <dt>Onboarding completed</dt>
            <dd>
              {data.onboardingCompletedAt === null
                ? 'Not completed'
                : new Date(data.onboardingCompletedAt).toLocaleString()}
            </dd>
            <dt>Record updated</dt>
            <dd>{new Date(data.updatedAt).toLocaleString()}</dd>
          </dl>
        </section>
      </div>

      <CustomerDocumentsPanel customerId={id} />

      <section className={styles.card} aria-labelledby="customer-applications-heading">
        <h2 className={styles.cardTitle} id="customer-applications-heading">
          Applications and contracts
        </h2>
        {applications.isPending && <PageSpinner label="Loading applications" />}
        {applications.isError && <Alert tone="error">{toDisplayMessage(applications.error)}</Alert>}
        {applications.isSuccess && applications.data.items.length === 0 && (
          <p className={styles.empty}>
            No application or contract is recorded for this customer yet.
          </p>
        )}
        {applications.isSuccess && applications.data.items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Customer applications and contracts">
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Product</th>
                  <th scope="col">Status</th>
                  <th scope="col">Financed</th>
                  <th scope="col">Updated</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {applications.data.items.map((application) => (
                  <tr key={application.id}>
                    <td>{application.contractReference ?? `APP-${application.id.slice(0, 8)}`}</td>
                    <td>
                      {application.requestedTerms?.productName ??
                        application.productNameSnapshot ??
                        '—'}
                    </td>
                    <td>
                      <StatusBadge status={application.status} />
                    </td>
                    <td>{formatNgn(application.financedAmount)}</td>
                    <td>{new Date(application.updatedAt).toLocaleDateString()}</td>
                    <td className={styles.rowAction}>
                      <Link to={routeTo.adminApplication(application.id)}>Open record</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!can(PERMISSIONS.SUBSCRIPTIONS_READ) && (
          <p className={styles.pageSubtitle}>Your role cannot read application records.</p>
        )}
      </section>

      <section className={styles.card} aria-labelledby="customer-addresses-heading">
        <h2 className={styles.cardTitle} id="customer-addresses-heading">
          Addresses
        </h2>
        {addresses.isPending && <PageSpinner label="Loading addresses" />}
        {addresses.isSuccess && addresses.data.length === 0 && (
          <p className={styles.empty}>
            No current address is recorded. The customer can add one from their profile.
          </p>
        )}
        {addresses.isSuccess && addresses.data.length > 0 && (
          <ul>
            {addresses.data.map((address) => (
              <li key={address.id}>
                {address.addressLine1}, {address.city}, {address.stateCode}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.card} aria-labelledby="customer-banks-heading">
        <h2 className={styles.cardTitle} id="customer-banks-heading">
          Bank accounts (masked)
        </h2>
        {bankAccounts.isPending && <PageSpinner label="Loading bank accounts" />}
        {bankAccounts.isSuccess && bankAccounts.data.length === 0 && (
          <p className={styles.empty}>
            No bank account is registered. A bank account is needed before direct debit setup.
          </p>
        )}
        {bankAccounts.isSuccess && bankAccounts.data.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Bank</th>
                  <th scope="col">Account name</th>
                  <th scope="col">Account number</th>
                  <th scope="col">Verification</th>
                </tr>
              </thead>
              <tbody>
                {bankAccounts.data.map((account) => (
                  <tr key={account.id}>
                    <td>{account.bankNameSnapshot}</td>
                    <td>{account.accountName}</td>
                    <td>{account.accountNumberMasked}</td>
                    <td>
                      <StatusBadge status={account.verificationStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
